import { useEffect, useState, useCallback, useRef } from 'react';
import useAuthStore from '../../../store/authStore';
import useQuizStore from '../../../store/quizStore';
import useCustomStore from '../../../store/customStore';

import api from '../../../services/api';
import MaThematiqueUpload from '../MaThematiqueUpload';
import MaThematiqueGenerations from '../MaThematiqueGenerations';
import TabQuiz from './TabQuiz';
import TabExamen from './TabExamen';
import TabEntrainement from './TabEntrainement';
import GuidedLearning from '../GuidedLearning';
import GuidedNextStep from '../../../components/quiz/GuidedNextStep';
import TabResultats from './TabResultats';
import { CheckSquare, FileText, Brain, BarChart3, Sparkles, Loader2, Settings, Compass, Plus } from 'lucide-react';
import Quiz from '../Quiz';
import ArcNav from '../../../components/ui/ArcNav';
import BaseModal from '../../../components/ui/BaseModal';
import TrialBanner from '../../../components/custom/TrialBanner';
import AiContentNotice from '../../../components/custom/AiContentNotice';

const TABS = [
    { id: 'quiz', label: 'S’entraîner', icon: CheckSquare },
    { id: 'examen', label: 'Mélange', icon: FileText },
    { id: 'entrainement', label: 'Progression', icon: Brain },
    { id: 'resultats', label: 'Mes résultats', icon: BarChart3 },
    { id: 'generations', label: 'Mes générations', icon: Sparkles },
];

/**
 * ScopeLandingInline — embedded version of ScopeLanding for user thématiques
 * Full feature parity with ScopeLanding (all tabs) but without header/scope nav
 */
export default function ScopeLandingInline({ scopeSlug, fallbackImage, activeTab: externalTab, onTabChange, gestionOpen = false, onOpenGestion = null, onCloseGestion = () => {}, onUnlock = null }) {
    const { user } = useAuthStore();
    const { startReview, startSmartQuiz, startExamenMelange } = useQuizStore();
    const activeJob = useCustomStore(s => s.activeJob);
    const prevJobRef = useRef(null);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const activeTab = externalTab || 'quiz';
    const [guidedOpen, setGuidedOpen] = useState(false);
    const [guidedAuto, setGuidedAuto] = useState(false); // ouvert par « Commencer » : lance la séance conseillée
    useEffect(() => { setGuidedOpen(false); }, [scopeSlug, activeTab]);
    const setActiveTab = (tab) => { if (onTabChange) onTabChange(tab); };
    const [openCategories, setOpenCategories] = useState({});
    // Catégorie présélectionnée dans la gestion quand on l'ouvre depuis la roue d'une catégorie
    // (null = ouverture depuis l'en-tête : aucune présélection ; 0 = « Non classé »).
    const [gestionCatId, setGestionCatId] = useState(null);
    const openGestionFor = (catId) => {
        setGestionCatId(typeof catId === 'number' ? catId : null);
        if (onOpenGestion) onOpenGestion();
    };
    const closeGestion = () => { setGestionCatId(null); onCloseGestion(); };

    // History + stats
    const [histLoading, setHistLoading] = useState(false);
    const [histEntries, setHistEntries] = useState([]);
    const [histLoaded, setHistLoaded] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsData, setStatsData] = useState(null);
    const [statsLoaded, setStatsLoaded] = useState(false);
    const [resultatsSub, setResultatsSubRaw] = useState(() => {
        try { return localStorage.getItem('monquiz_resultats_subtab') || 'historique'; } catch { return 'historique'; }
    });
    const setResultatsSub = (tab) => { setResultatsSubRaw(tab); try { localStorage.setItem('monquiz_resultats_subtab', tab); } catch {} };

    // Entrainement (mastery)
    const [masteryLoading, setMasteryLoading] = useState(false);
    const [masteryData, setMasteryData] = useState(null);
    const [masteryLoaded, setMasteryLoaded] = useState(false);
    const [smartQuizLoading, setSmartQuizLoading] = useState(false);
    const [smartQuizActive, setSmartQuizActive] = useState(false);

    // Errors
    const [errorMode, setErrorMode] = useState('never');
    const [errorCatId, setErrorCatId] = useState(0);
    const [errorLoading, setErrorLoading] = useState(false);
    const [errorSearchDone, setErrorSearchDone] = useState(false);
    const [errorSujets, setErrorSujets] = useState([]);
    const [errorPlayable, setErrorPlayable] = useState(0); // erreurs encore rejouables (droits, concours, en ligne)
    const [errorTotal, setErrorTotal] = useState(0);
    const [errorPages, setErrorPages] = useState(0);
    const [errorPage, setErrorPage] = useState(1);
    const [errorQuizActive, setErrorQuizActive] = useState(false);
    const [errorQuizLoading, setErrorQuizLoading] = useState(false);

    // Examen blanc
    const [examenNbQuestions, setExamenNbQuestions] = useState(20);
    const [examenCategories, setExamenCategories] = useState([]);
    const [examenDiffMin, setExamenDiffMin] = useState(1);
    const [examenDiffMax, setExamenDiffMax] = useState(3);
    const [examenLoading, setExamenLoading] = useState(false);
    const [examenActive, setExamenActive] = useState(false);

    // Quiz modal
    const [modalSousSujetId, setModalSousSujetId] = useState(null);
    const [modalMode, setModalMode] = useState(null);
    const [modalNbQuestions, setModalNbQuestions] = useState(null);
    const [modalQuestionIds, setModalQuestionIds] = useState(null);
    const [modalOrigin, setModalOrigin] = useState('subject');

    // Toggle to show/hide OBSOLETE user sujets (filter client-side only).
    const [showObsolete, setShowObsolete] = useState(false);

    const fetchData = useCallback(() => {
        return api.get('/scopes/detail.php', { params: { slug: scopeSlug, include_obsolete: 1 } })
            .then(({ data: res }) => {
                setData(res.data);
                const cats = res.data?.categories || [];
                if (cats.length === 0) return;
                setOpenCategories(prev => {
                    const validOpen = Object.keys(prev).filter(id => prev[id] && cats.some(c => String(c.id) === String(id)));
                    if (validOpen.length > 0) return prev;
                    return { [cats[0].id]: true };
                });
            })
            .catch(e => { console.warn('Erreur chargement thématique:', e); setData(null); });
    }, [scopeSlug]);

    useEffect(() => {
        setLoading(true);
        setHistLoaded(false);
        setStatsLoaded(false);
        setMasteryLoaded(false);
        fetchData().finally(() => setLoading(false));
    }, [scopeSlug]);

    // Auto-refresh when a generation job completes
    useEffect(() => {
        // QUEUED aussi : un job rapide (ou l'essai, mis en file) peut passer directement à COMPLETED.
        if (['QUEUED', 'PROCESSING'].includes(prevJobRef.current?.status) && activeJob?.status === 'COMPLETED') {
            fetchData();
        }
        prevJobRef.current = activeJob;
    }, [activeJob]);

    // Load tab data
    useEffect(() => {
        if (!data || !user) return;
        const scopeId = data.scope.id;
        if (activeTab === 'resultats') {
            if (!histLoaded) {
                setHistLoading(true);
                api.get('/qcm/history.php', { params: { scope_id: scopeId } })
                    .then(({ data: res }) => { setHistEntries(res.data || []); setHistLoaded(true); })
                    .catch(e => console.warn('Erreur chargement:', e)).finally(() => setHistLoading(false));
            }
            if (!statsLoaded) {
                setStatsLoading(true);
                api.get('/user/stats.php', { params: { scope_id: scopeId } })
                    .then(({ data: res }) => { setStatsData(res.data); setStatsLoaded(true); })
                    .catch(e => console.warn('Erreur chargement:', e)).finally(() => setStatsLoading(false));
            }
        }
        if (activeTab === 'entrainement' && !masteryLoaded) {
            setMasteryLoading(true);
            api.get('/user/mastery.php', { params: { scope_id: scopeId } })
                .then(({ data: res }) => { setMasteryData(res.data); setMasteryLoaded(true); })
                .catch(e => console.warn('Erreur chargement:', e)).finally(() => setMasteryLoading(false));
        }
        // Flags dans les deps : onQuizDone (fin de quiz / retour d'apprentissage guidé) les
        // remet à false → l'effet se relance et refetch l'onglet courant sans changer d'onglet.
    }, [activeTab, data, user, masteryLoaded, histLoaded, statsLoaded]);

    if (loading) return <div className="mt-inline-loading"><Loader2 size={20} className="spin" /> Chargement...</div>;
    if (!data) return <div className="mt-inline-empty">Thématique introuvable.</div>;

    const { scope: scopeInfo, has_access, is_shared, formateur_tracked, categories } = data;
    const trial = data.trial || null; // essai gratuit (migration 181) : { state, days_left, ends_at, … }
    // Bilan : il compare les sujets pour dire par où commencer → au moins 2 sujets jouables.
    const nbSujetsJouables = (categories || []).reduce((n, c) => n + (c.sous_sujets || []).filter(ss => (ss.nb_questions || 0) > 0).length, 0);
    const BILAN_MIN_SUJETS = 2;
    const thematiqueId = scopeInfo.thematique_id;

    const toggleCategory = (catId) => {
        setOpenCategories(prev => {
            const isOpen = !!prev[catId];
            if (!isOpen) {
                setTimeout(() => {
                    document.getElementById(`cat-${catId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 50);
            }
            return isOpen ? {} : { [catId]: true };
        });
    };

    // Lien « Voir le sujet » du bandeau de fin de génération : ferme la gestion, revient à l'onglet
    // Quiz, ouvre la catégorie du sujet, puis défile jusqu'à sa carte et la met brièvement en évidence.
    // La liste est rechargée à la fin du job (effet plus haut) : on attend que la carte existe.
    const goToSousSujet = (sousSujetId) => {
        closeGestion();
        setGuidedOpen(false);
        setActiveTab('quiz');
        let tries = 0;
        const tryGo = () => {
            const cat = (data?.categories || []).find(c => c.sous_sujets.some(ss => ss.id === sousSujetId));
            if (cat) setOpenCategories(prev => (prev[cat.id] ? prev : { [cat.id]: true }));
            const el = document.querySelector(`[data-ss-id="${sousSujetId}"]`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('sl-card-flash');
                setTimeout(() => el.classList.remove('sl-card-flash'), 2400);
                return;
            }
            if (tries++ < 30) setTimeout(tryGo, 100);
        };
        setTimeout(tryGo, 80);
    };

    const handleStartSousSujet = (sousSujetId, sessionMode, nbQuestions) => {
        setModalSousSujetId(sousSujetId);
        setModalMode(sessionMode || 'apprentissage');
        setModalNbQuestions(null);
        setModalQuestionIds(null);
        setModalOrigin('subject');
    };

    const directLaunch = (sousSujetId, mode, nb) => {
        setModalSousSujetId(sousSujetId);
        setModalMode(mode);
        setModalNbQuestions(nb);
        setModalQuestionIds(null);
        setModalOrigin('subject');
    };

    const searchErrors = async (p = 1) => {
        setErrorLoading(true);
        setErrorSearchDone(false);
        try {
            const { data: res } = await api.post('/qcm/errors.php', {
                scope_id: scopeInfo.id, mode: errorMode,
                categorie_id: errorCatId || undefined, page: p,
            });
            const r = res.data;
            setErrorSujets(r.by_sujet || []); setErrorPlayable(r.playable_total ?? r.total_questions); setErrorTotal(r.total_questions);
            setErrorPages(r.total_pages); setErrorPage(r.page);
            setErrorSearchDone(true);
        } catch (e) { console.warn('Erreur recherche:', e); setErrorSujets([]); setErrorPlayable(0); setErrorTotal(0); setErrorSearchDone(true); }
        setErrorLoading(false);
    };

    // Error quiz now handled by quizStore.startErrorsQuiz + errorQuizActive overlay

    const requireAccess = (action) => {
        if (!has_access) return false;
        if (action) action();
        return true;
    };

    return (
        <div className="mt-inline">
            {/* Upload retiré d'ici — déplacé dans TabQuiz (section "Gestion des sujets") via renderGestionSujets */}

            {user && has_access && guidedOpen && activeTab !== 'resultats' && (
                <GuidedLearning embedded scopeSlugProp={scopeSlug} autoLaunch={guidedAuto}
                    onCloseEmbedded={() => { setGuidedOpen(false); setGuidedAuto(false); setMasteryLoaded(false); setHistLoaded(false); setStatsLoaded(false); fetchData(); }} />
            )}
            {/* Decision mode dock — hidden in history mode */}
            {!guidedOpen && activeTab !== 'resultats' && (
                <ArcNav
                    activeTab={activeTab}
                    onTabChange={(tabId) => setActiveTab(tabId)}
                    scopeColor={scopeInfo?.couleur_theme}
                    scopeId={scopeInfo?.id}
                    categories={categories}
                    examenNbQuestions={examenNbQuestions}
                    hasUser={!!user}
                    nextStep={user && has_access ? (nbSujetsJouables < BILAN_MIN_SUJETS ? (
                        nbSujetsJouables === 0 ? null : <>
                            <div className="guided-next" aria-live="polite">
                                <Compass className="guided-next__icon" size={26} aria-hidden="true" />
                                <div className="guided-next__copy">
                                    <span className="guided-next__eyebrow">Bilan de connaissances</span>
                                    <strong className="guided-next__title">Il faut au moins {BILAN_MIN_SUJETS} sujets pour faire votre bilan</strong>
                                    <span className="guided-next__sub">Le bilan compare vos sujets pour vous dire par où commencer. Ajoutez-en un : votre document, une idée{trial ? ' ou un sujet d’exemple' : ''}.</span>
                                </div>
                                {onOpenGestion && (
                                    <div className="guided-next__actions">
                                        <button type="button" className="btn btn-primary" onClick={() => openGestionFor()}>
                                            <Plus size={16} /> Ajouter un sujet
                                        </button>
                                    </div>
                                )}
                            </div>
                            <span className="mode-dock__or">Ou choisis toi-même</span>
                        </>
                    ) : (
                        <GuidedNextStep key={scopeSlug} scopeSlug={scopeSlug}
                            onLaunch={() => { setGuidedAuto(true); setGuidedOpen(true); }}
                            onOpen={() => { setGuidedAuto(false); setGuidedOpen(true); }} />
                    )) : null}
                />
            )}

            {trial && !guidedOpen && (
                <TrialBanner trial={trial} activeJob={activeJob} onUnlock={onUnlock}
                    onOpenGestion={onOpenGestion ? () => openGestionFor() : null} />
            )}

            {!guidedOpen && <AiContentNotice variant="line" />}

            {/* Tab content */}
            {!guidedOpen && <div className="mt-inline__content">
                {activeTab === 'quiz' && (
                    <TabQuiz
                        categories={categories} has_access={has_access} user={user}
                        openCategories={openCategories} toggleCategory={toggleCategory}
                        onStart={handleStartSousSujet} onDirectLaunch={directLaunch}
                        onResume={handleStartSousSujet}
                        onLocked={trial && onUnlock ? onUnlock : () => {}} isCompte={true} scope={scopeSlug}
                        isTrial={!!trial}
                        isMaThematique={true} thematiqueId={thematiqueId} onRefresh={fetchData}
                        showObsolete={showObsolete}
                        onToggleShowObsolete={() => setShowObsolete(v => !v)}
                        onOpenGestion={onOpenGestion ? openGestionFor : null}
                        accentColor={scopeInfo?.couleur_theme}
                    />
                )}
                {activeTab === 'examen' && (
                    <TabExamen
                        scopeInfo={scopeInfo} categories={categories}
                        has_access={has_access} user={user}
                        examenNbQuestions={examenNbQuestions} setExamenNbQuestions={setExamenNbQuestions}
                        examenCategories={examenCategories} setExamenCategories={setExamenCategories}
                        examenDiffMin={examenDiffMin} setExamenDiffMin={setExamenDiffMin}
                        examenDiffMax={examenDiffMax} setExamenDiffMax={setExamenDiffMax}
                        examenLoading={examenLoading} setExamenLoading={setExamenLoading}
                        examenActive={examenActive} setExamenActive={setExamenActive}
                        requireAccess={requireAccess} quizOverlayBg={{}}
                    />
                )}
                {activeTab === 'entrainement' && (
                    <TabEntrainement
                        scopeInfo={scopeInfo} categories={categories}
                        user={user} has_access={has_access}
                        masteryLoading={masteryLoading} masteryData={masteryData}
                        smartQuizLoading={smartQuizLoading} setSmartQuizLoading={setSmartQuizLoading}
                        smartQuizActive={smartQuizActive} setSmartQuizActive={setSmartQuizActive}
                        onQuizDone={() => { setMasteryLoaded(false); setHistLoaded(false); setStatsLoaded(false); }}
                        errorMode={errorMode} setErrorMode={setErrorMode}
                        errorCatId={errorCatId} setErrorCatId={setErrorCatId}
                        errorLoading={errorLoading} errorSearchDone={errorSearchDone}
                        setErrorSearchDone={setErrorSearchDone}
                        errorSujets={errorSujets} errorPlayable={errorPlayable} errorTotal={errorTotal}
                        errorPages={errorPages} errorPage={errorPage}
                        errorQuizActive={errorQuizActive} setErrorQuizActive={setErrorQuizActive}
                        errorQuizLoading={errorQuizLoading} setErrorQuizLoading={setErrorQuizLoading}
                        requireAccess={requireAccess} searchErrors={searchErrors}
                        quizOverlayBg={{}}
                        onGoToQuiz={() => setActiveTab('quiz')}
                    />
                )}
                {activeTab === 'resultats' && (
                    <TabResultats
                        scopeInfo={scopeInfo} user={user} has_access={has_access}
                        histLoading={histLoading} histEntries={histEntries}
                        statsLoading={statsLoading} statsData={statsData}
                        resultatsSub={resultatsSub} setResultatsSub={setResultatsSub}
                        startReview={startReview}
                        setModalSousSujetId={setModalSousSujetId} setModalMode={setModalMode}
                        setModalNbQuestions={setModalNbQuestions} setModalQuestionIds={setModalQuestionIds}
                        setModalOrigin={setModalOrigin}
                        onGoToQuiz={() => setActiveTab('quiz')}
                        onHistoryCleared={(!is_shared && !formateur_tracked) ? () => { setHistLoaded(false); setStatsLoaded(false); fetchData(); } : undefined}
                        showGenerations={false}
                        isOwnerThematique={true}
                        thematiqueId={thematiqueId}
                    />
                )}
            </div>}

            {/* Gestion des sujets — modale ouverte depuis le bouton du header (hub) */}
            <BaseModal
                open={gestionOpen && (has_access || !!trial)}
                onClose={closeGestion}
                title="Gestion des sujets"
                icon={<Settings size={16} />}
                maxWidth="980px"
            >
                {(has_access || trial) && (
                    <MaThematiqueUpload
                        scopeId={scopeInfo.id}
                        thematiqueId={thematiqueId}
                        onGenerated={() => fetchData()}
                        onOpenSujet={goToSousSujet}
                        initialCategoryId={gestionCatId}
                        trial={trial}
                        onUnlock={onUnlock}
                        forceExpanded
                    />
                )}
            </BaseModal>

            {/* Quiz modal — same pattern as normal scopes */}
            {modalSousSujetId && (() => {
                // Find category image for the active sous-sujet (priority: category > scope > fallback)
                const activeCat = categories.find(c => c.sous_sujets.some(ss => ss.id === modalSousSujetId));
                const bgImage = activeCat?.image || scopeInfo?.image || fallbackImage;
                const overlayStyle = bgImage ? { backgroundImage: `url(${bgImage})` } : {};
                return (
                <div className={`quiz-modal-overlay ${bgImage ? 'has-bg-image' : ''}`} style={overlayStyle}>
                    <Quiz
                        sousSujetIdProp={modalSousSujetId} scopeIdProp={scopeInfo.id}
                        scopeInfoProp={{ ...scopeInfo, image: scopeInfo.image || fallbackImage }}
                        nbQuestionsProp={modalNbQuestions} modeProp={modalMode}
                        questionIdsProp={modalQuestionIds} originProp={modalOrigin}
                        isCustomProp={true}
                        onClose={() => { useQuizStore.getState().reset(); setModalSousSujetId(null); fetchData(); }}
                    />
                </div>
                );
            })()}
        </div>
    );
}
